import { Cpu, Loader2 } from "lucide-react";
import type { WSStatus } from "../types";

interface HeaderProps {
  wsStatus: WSStatus;
  sessionId: string | null;
}

export default function Header({ wsStatus, sessionId }: HeaderProps) {
  return (
    <header className="relative flex items-center justify-between px-6 py-3 bg-dark-surface border-b border-dark-border glass z-10">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cf-orange via-orange-400 to-transparent" />

      {/* Left: Cloudflare logo + demo title */}
      <div className="flex items-center gap-4">
        <CloudflareLogo />
        <div className="h-5 w-px bg-dark-border-2" />
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-cf-orange" />
          <span className="text-sm font-semibold tracking-wide text-slate-200">
            AI Agent Demo
          </span>
        </div>
      </div>

      {/* Right: session + WS status */}
      <div className="flex items-center gap-3">
        {sessionId && (
          <span className="hidden sm:flex items-center gap-1.5 text-[11px] font-mono text-slate-500 bg-dark-bg px-2.5 py-1 rounded border border-dark-border">
            <span className="text-slate-600">sess:</span>
            <span className="text-slate-400">{sessionId.slice(0, 8)}</span>
          </span>
        )}
        <WSBadge status={wsStatus} />
      </div>
    </header>
  );
}

function WSBadge({ status }: { status: WSStatus }) {
  const configs: Record<WSStatus, { dot: string; label: string; ring: string }> = {
    connected:    { dot: "bg-emerald-400 shadow-[0_0_6px_#34d399]", label: "Connected",    ring: "border-emerald-900/50 bg-emerald-950/30 text-emerald-400" },
    connecting:   { dot: "bg-amber-400 animate-pulse",               label: "Connecting…",  ring: "border-amber-900/50 bg-amber-950/30 text-amber-400" },
    disconnected: { dot: "bg-slate-600",                              label: "Disconnected", ring: "border-slate-700/50 bg-slate-900/30 text-slate-500" },
    error:        { dot: "bg-red-500 shadow-[0_0_6px_#ef4444]",      label: "Error",        ring: "border-red-900/50 bg-red-950/30 text-red-400" },
  };
  const c = configs[status];
  return (
    <span className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border ${c.ring}`}>
      {status === "connecting" ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <span className={`w-2 h-2 rounded-full ${c.dot}`} />
      )}
      {c.label}
    </span>
  );
}

function CloudflareLogo() {
  return (
    <div className="flex items-center gap-2.5 select-none">
      {/* Official Cloudflare logo */}
      <svg viewBox="-1 -1 67 35" className="h-8 w-auto" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M45.268 26.469l1.208-4.163c.395-1.363.187-2.62-.586-3.543-.72-.858-1.867-1.363-3.198-1.44l-22.106-.31c-.155-.012-.298-.086-.378-.209a.471.471 0 01-.05-.421c.074-.223.285-.372.521-.384l22.295-.31c2.697-.137 5.63-2.312 6.596-4.897l1.221-3.27a.803.803 0 00.044-.453C48.71 3.09 44.975 0 40.518 0c-3.82 0-7.09 2.276-8.561 5.554a7.29 7.29 0 00-5.198-1.242 7.342 7.342 0 00-6.283 5.727 10.378 10.378 0 00-7.222 3.64A10.217 10.217 0 0011 19.162a10.37 10.37 0 00.161 1.812C5.006 21.083 0 26.154 0 32h48.803a.487.487 0 00.472-.359l.019-.067-4.026-5.105z"
          fill="#F6821F"
        />
        <path
          d="M53.346 10.044a.257.257 0 00-.05-.012 8.876 8.876 0 00-.415-.025h-.236c-.087 0-.174.006-.26.012a.39.39 0 00-.323.261l-.87 2.998c-.395 1.363-.187 2.62.586 3.544.72.857 1.867 1.363 3.198 1.44l4.674.309c.155.012.298.087.378.21a.471.471 0 01.05.42.515.515 0 01-.521.385l-4.862.31c-2.71.136-5.63 2.312-6.596 4.896l-.342.932a.247.247 0 00.236.347H65V17.72c0-4.35-3.552-7.887-7.903-7.886a7.972 7.972 0 00-3.75.93v-.72z"
          fill="#FBAD41"
        />
      </svg>
      {/* Wordmark */}
      <span className="text-lg font-bold text-white tracking-tight leading-none">
        Cloudflare
      </span>
    </div>
  );
}
